/**
 * useExportShortcut Hook
 * Handles ALT+E keyboard shortcut for export functionality
 */

import { useEffect, useCallback } from 'react';

/**
 * Custom hook to handle ALT+E export shortcut
 * @param {Function} onExport - Callback function when ALT+E is pressed
 * @param {boolean} enabled - Whether the shortcut is enabled (default: true)
 */
const useExportShortcut = (onExport, enabled = true) => {
  const handleKeyDown = useCallback((event) => {
    // Check if ALT + E is pressed
    if (event.altKey && (event.key === 'e' || event.key === 'E')) {
      // Don't trigger if user is typing in an input, textarea, or contenteditable
      const activeElement = document.activeElement;
      const tagName = activeElement?.tagName?.toLowerCase();
      const isEditable = activeElement?.isContentEditable;
      const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select';
      
      // Check if it's a text input (not checkbox, radio, etc.)
      const inputType = activeElement?.type?.toLowerCase();
      const isTextInput = isInput && (!inputType || ['text', 'email', 'password', 'search', 'tel', 'url', 'number', 'date', 'datetime-local', 'month', 'time', 'week'].includes(inputType));

      if (isTextInput || isEditable) {
        return; // Don't trigger export when typing
      }

      event.preventDefault();
      event.stopPropagation();
      onExport();
    }
  }, [onExport]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);
};

export default useExportShortcut;
